import React, { useEffect, useState } from 'react';
import { Flex, Grid, Text } from '@chakra-ui/react';
import useNetworks from '../../../providers/Networks/useNetworks';
import { useBlockchainProvider } from '../../../providers/Blockchain/blockchainProvider';
import { OnboardingBar } from './onboardingBar';
import { OnboardingTile } from './onboardingTile';
import { createStandaloneToast } from '@chakra-ui/react';
import { ethers } from 'ethers';
import { blockchainConfig } from '../../../blockchain-config';
import { containerMaxWXl } from 'src/components/ContentContainer/contentContainer';
import { redirectToChromeStore, redirectToRampNetwork } from 'src/utils/redirector';

export interface ITile {
	isCompleted: boolean | null;
	action: () => void;
	content: {
		header: {
			title: string;
			iconUri: string;
		};
		description: string;
		external?: {
			uri?: string;
			text?: string;
		};
		enumerator: string;
	};
	isHalfCompletable: boolean;
}

const defaultTile: ITile = {
	isCompleted: false,
	action: () => null,
	content: {
		header: { iconUri: '', title: '' },
		description: '',
		external: {
			uri: '/',
			text: 'Instruction',
		},
		enumerator: '…',
	},
	isHalfCompletable: false,
};

export const Onboarding = () => {
	const [tileCompletionCount, setTileCompletionCount] = useState(0);
	const [isUsdcCompleted, setIsUsdcCompleted] = useState<boolean | null>(null);

	const { switchNetwork } = useNetworks();
	const { chainId, isEthereum, provider, account } = useBlockchainProvider();
	const { toast } = createStandaloneToast();

	const handleClaimYourGame = () => toast({ title: 'Coming soon' });
	const handleIsUsdcCompleted = async () => {
		if (
			isEthereum &&
			provider &&
			account !== ethers.constants.AddressZero &&
			chainId === blockchainConfig.NETWORK_DEFAULT.ID
		) {
			const genericErc20Abi = require('../../../contracts/erc20/abi.json');
			const tokenContractAddress = '0x2791bca1f2de4661ed88a30c99a7a9449aa84174';
			try {
				const contract = new ethers.Contract(tokenContractAddress, genericErc20Abi, provider);
				const balance = (await contract.balanceOf(account)).toString();

				return !isNaN(balance) && Number(balance) !== 0;
			} catch {}
		}

		return null;
	};

	const tiles: ITile[] = [
		{
			...defaultTile,
			action: redirectToChromeStore,
			content: {
				header: { iconUri: '/icons/metamask.svg', title: 'Install MetaMask' },
				description: 'Create your first cryptowallet',
				enumerator: '1.',
			},
			isCompleted: isEthereum,
		},
		{
			...defaultTile,
			action: () => void switchNetwork(),
			content: {
				header: { iconUri: '/icons/polygon.svg', title: 'Add Polygon' },
				description: 'Set up MetaMask for Polygon',
				enumerator: '2.',
			},
			isCompleted: isEthereum && blockchainConfig.NETWORK_DEFAULT.ID === chainId,
		},
		{
			...defaultTile,
			action: redirectToRampNetwork,
			content: {
				/** TODO: Mainnet USDC address check */
				header: { iconUri: '/icons/usdc.svg', title: 'Buy some USDC' },
				description: 'Get your first stablecoin!',
				enumerator: '3.',
			},
			isHalfCompletable: true,
			isCompleted: isUsdcCompleted,
		},
		{
			...defaultTile,
			content: {
				/** TODO: Mainnet TFE (2048) address check */
				header: { iconUri: '/icons/tfe.svg', title: 'Claim your game!' },
				description: '2048 is waiting your you!',
				external: { text: 'Get your game' },
				enumerator: '4.',
			},
			action: handleClaimYourGame,
		},
	];

	const isAllCompleted = tiles.map(tile => tile.isCompleted).every(Boolean);

	useEffect(() => setTileCompletionCount(tiles.filter(tile => tile.isCompleted).length), [tiles]);

	useEffect(() => {
		if (!provider) {
			return;
		}

		(async () => {
			setIsUsdcCompleted(await handleIsUsdcCompleted());
		})();
	}, [account, provider]);

	return (
		<Flex
			display={isAllCompleted ? 'none' : 'flex'}
			position="relative"
			flexDirection="column"
			width="100%"
			maxW={containerMaxWXl}
			paddingX={{ base: '16px', hashup: '0' }}
			gridGap={{ base: '16px', hashup: '0' }}
			marginBottom={{ base: '0', hashup: '-48px' }}
			marginTop={{ base: '0', hashup: '-32px' }}
		>
			<Flex
				align="center"
				gap="40px"
				paddingBottom={{ base: '0', hashup: '18px' }}
			>
				<Text
					fontSize={{ base: '16px', hashup: '16px' }}
					lineHeight="29px"
				>
					Before you start —{' '}
					<Text
						as="span"
						color="action"
					>
						4
					</Text>{' '}
					simple steps
				</Text>
				<OnboardingBar completionCount={tileCompletionCount} />
			</Flex>

			<Flex
				display={{ base: 'grid', hashup: 'flex' }}
				position="relative"
				overflowX="scroll"
				flexDirection="column"
				css={{
					'&::-webkit-scrollbar': {
						width: '0 !important',
					},
					overflow: '-moz-scrollbars-none',
					msOverflowStyle: 'none',
				}}
			>
				<Grid
					gridRowGap="18px"
					gridColumnGap="18px"
					flexWrap={{ base: 'nowrap', hashup: 'wrap' }}
					overflowX="scroll"
					width={{ base: '100%', hashup: '100%' }}
					margin="0 auto"
					gridTemplateColumns={{ base: '1fr', hashup: 'repeat(4,1fr)' }}
					css={{
						'&::-webkit-scrollbar': {
							width: '0 !important',
						},
						overflow: '-moz-scrollbars-none',
						msOverflowStyle: 'none',
					}}
				>
					{tiles.map(tile => (
						<OnboardingTile
							key={tile.content.header.title}
							{...tile}
						/>
					))}
				</Grid>
			</Flex>
		</Flex>
	);
};
